import React, {useState, useEffect} from 'react';
import t from 'babel-plugin-yate/macro';
import {useDispatch, useSelector} from 'react-redux';
import get from 'lodash-es/get';
import {Form, Field} from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import {FieldArray} from 'react-final-form-arrays';

import {
  getOccupation,
  getCivilStatus,
  getEducation,
  getPropertyStatus,
  getEmploymentStatuses,
  getFundingSource,
  getAccountStatementDeliveryMode,
  getAddressType,
  getCities,
} from '../../store/request';

import {
  formatDateFormField,
  formatNumberFormField,
  formatWhiteSpaceFormField,
  formatNonWhiteSpaceFormField,
  parseDateFormField,
  parseNumberFormField,
  parseRadioFormField,
} from '../../lib/misc';

import {
  composeFormValidators,
  hasMaxLength,
  isAlphaExtraNumeric,
  isAlphaNumeric,
  isEmail,
  isNumber,
  isRequired,
  isValidDate,
  hasMinVal,
  isPhoneNumber,
  isIBAN,
  isValidYear,
  hasItems,
} from '../../lib/validations';
import {
  getCurrentStoredAddressLabel,
  getAddressDisabledStatus,
} from '../../config/cardDeliveryAddressHelpers';

import {
  ANAF_CODE,
  MAI_MAPN_CODE,
  PENSIONARY_CODE,
} from '../../config/employmentStatuses';

import {Block} from '../../components/uiElem';
import {Input} from '../../components/Form/Input';
import {DocumentsUploadBox} from '../../components/DocumentsUploadBox';
import Label from '../../components/Form/Label';
import {Select} from '../../components/Form/Select';
import RadioButton from '../../components/Form/RadioButton';
import ValidationError from '../../components/Form/ValidationError';
import {Products} from '../Products';
import Condition from '../../components/Condition';
import Checkbox from '../../components/Form/Checkbox';
import Address from '../../components/Address';
import RefinancingCreditsForm from './RefinancingCreditsForm';
import {useUploadFile} from '../../lib/useUploadFile';

const DOC_TYPE_INCOME = 'income';
const DOC_TYPE_BANK_STATEMENT = 'bank_statement';

const AdditionalInformationForm = ({
  handleAction,
  requestType,
  disabled,
  formRef,
  initialValues,
  type,
  uuid,
  showDeliveryAddressCcValue,
  showRefinancingCreditsForm,
  patchHandler,
}) => {
  const dispatch = useDispatch();
  const {file, uploading, uploaded, fetch, upload} = useUploadFile({
    cbOnSuccess: () => patchHandler({}, true),
  });

  const {
    civilStatus,
    accountStatementDeliveryMode,
    education,
    employmentStatuses,
    occupation,
    propertyStatus,
    fundingSource,
    addressType,
    cities,
  } = useSelector(state => state.request);

  const [forcedIbanIds, setForcedIbanIds] = useState(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const correspondenceRegionId =
    initialValues.customer.correspondence_address?.region_id;
  const residenceRegionId = initialValues.customer.residence_address?.region_id;
  const customerProfessionalCodes = Boolean(
    initialValues?.customer?.professional_status,
  )
    ? initialValues.customer.professional_status.map(({code}) => code)
    : [];

  // const isPensioner = customerProfessionalCodes.includes(PENSIONARY_CODE);
  const isEmployedWithANAF = customerProfessionalCodes.includes(ANAF_CODE);
  const isEmployedWithoutANAF =
    customerProfessionalCodes.includes(MAI_MAPN_CODE);
  const isConsumerFinance = type === 4;
  const isPersonalNeeds = type === 5;
  const isCreditCard = type === 6;
  const showDeliveryAddressCc = isCreditCard && showDeliveryAddressCcValue;

  useEffect(() => {
    const fetchData = async () => {
      if (!occupation) dispatch(getOccupation(uuid));
      if (!civilStatus) dispatch(getCivilStatus(uuid));
      if (!education) dispatch(getEducation(uuid));
      if (!propertyStatus) dispatch(getPropertyStatus(uuid));
      if (!fundingSource) dispatch(getFundingSource(uuid));
      if (!accountStatementDeliveryMode)
        dispatch(getAccountStatementDeliveryMode(uuid));
      if (!employmentStatuses) dispatch(getEmploymentStatuses(uuid));
      if (!addressType) dispatch(getAddressType(uuid));

      setIsLoading(false);

      if (correspondenceRegionId && !cities[correspondenceRegionId]) {
        dispatch(getCities(correspondenceRegionId, uuid));
      }

      if (residenceRegionId && !cities[residenceRegionId]) {
        dispatch(getCities(residenceRegionId, uuid));
      }
    };

    fetchData();
  }, [
    uuid,
    occupation,
    civilStatus,
    education,
    propertyStatus,
    fundingSource,
    accountStatementDeliveryMode,
    employmentStatuses,
    addressType,
    dispatch,
    correspondenceRegionId,
    residenceRegionId,
    cities,
  ]);

  useEffect(() => {
    !isEmployedWithANAF && fetch(DOC_TYPE_INCOME);
    fetch(DOC_TYPE_BANK_STATEMENT);
  }, [isEmployedWithANAF, fetch]);

  if (isLoading) {
    return (
      <div className="min-h-200 flex items-center justify-center text-30">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <Form
      keepDirtyOnReinitialize={true}
      onSubmit={handleAction}
      initialValues={initialValues}
      mutators={arrayMutators}
      render={({handleSubmit, values, submitFailed, submitSucceeded, form}) => {
        if (
          !get(values, 'customer.political.is_exposed') &&
          get(values, 'customer.political.function')
        ) {
          values.customer.political.function = '';
        }

        if (
          Boolean(values.customer?.employment?.permanent_contract) &&
          Boolean(values.customer?.employment?.job_ends_at)
        ) {
          /**
           * when switching 'Fixed term appointment' from 'Yes' to 'No',
           * clean the associated field's value
           */
          values.customer.employment.job_ends_at = '';
        }

        const cardDeliveryAddressId = get(
          values,
          'customer.card_data.address_type',
        );
        const hasEmail = !!get(values, 'customer.email');
        const hasInitialFullAddressId = !!get(
          initialValues,
          'customer.identity_card.address.full_address',
        );
        const hasInitialBirthPlace = !!get(
          initialValues,
          'customer.identity_card.birth_place',
        );

        return (
          <>
            <form onSubmit={handleSubmit} ref={formRef}>
              <div className="lg:flex -ml-10">
                <div className="lg:w-1/2 pl-10">
                  {isPersonalNeeds && showRefinancingCreditsForm && (
                    <RefinancingCreditsForm
                      disabled={disabled}
                      uuid={uuid}
                      onForceIbanChange={setForcedIbanIds}
                    />
                  )}

                  <Block heading={t`Employment information`}>
                    <div className="pt-8 p-2">
                      <div className="md:flex md:flex-wrap">
                        {!customerProfessionalCodes.includes(
                          PENSIONARY_CODE,
                        ) && (
                          <div className="w-full md:w-1/2 px-2 mb-4">
                            <Field
                              name="customer.detail.occupation_type_id"
                              label={t`Occupation type`}
                              component={Select}
                              disabled={disabled}
                              parse={parseNumberFormField}
                              validate={isRequired()}>
                              <option value="">{t`Select occupation`}</option>

                              {occupation &&
                                occupation.map(({id, name}) => (
                                  <option
                                    key={`occupation-select-${id}`}
                                    value={id}>
                                    {name}
                                  </option>
                                ))}
                            </Field>
                          </div>
                        )}

                        {[MAI_MAPN_CODE, PENSIONARY_CODE].some(el =>
                          customerProfessionalCodes.includes(el),
                        ) && (
                          <div className="w-full md:w-1/2 px-2 mb-4">
                            <Field
                              name={
                                values?.customer?.employment?.pension
                                  ? 'customer.employment.pension'
                                  : 'customer.employment.monthly_income'
                              }
                              label={t`Monthly net income`}
                              component={Input}
                              disabled={disabled}
                              step="0.01"
                              type="number"
                              parse={parseNumberFormField}
                              validate={composeFormValidators(
                                isRequired(),
                                isNumber(),
                                hasMinVal(0),
                              )}
                            />
                          </div>
                        )}

                        {(isEmployedWithANAF || isEmployedWithoutANAF) && (
                          <>
                            {isEmployedWithoutANAF && (
                              <div className="w-full px-2 mb-4">
                                <Field
                                  name="customer.employment.employer_name"
                                  label={t`Employer Company`}
                                  component={Input}
                                  disabled={disabled}
                                  validate={composeFormValidators(
                                    isRequired(),
                                    isAlphaExtraNumeric(),
                                    hasMaxLength(64),
                                  )}
                                />
                              </div>
                            )}

                            {isEmployedWithoutANAF && (
                              <>
                                <div className="w-full md:w-1/2 px-2 mb-4">
                                  <Field
                                    name="customer.employment.job_started_at"
                                    label={t`Employment start date`}
                                    component={Input}
                                    placeholder={t`DD.MM.YYYY`}
                                    disabled={disabled}
                                    format={formatDateFormField}
                                    parse={parseDateFormField}
                                    validate={composeFormValidators(
                                      isRequired(),
                                      isValidDate(),
                                      isValidYear(),
                                    )}
                                  />
                                </div>

                                <div className="w-full md:w-1/2 px-2 mb-4">
                                  <Label value={t`Fixed term appointment`} />
                                  <div className="flex mt-4">
                                    <div className="pr-8">
                                      <Field
                                        name="customer.employment.permanent_contract"
                                        component={RadioButton}
                                        type="radio"
                                        value={false}
                                        disabled={disabled}
                                        parse={parseRadioFormField}
                                        label={t`Yes`}
                                      />
                                    </div>

                                    <div className="pr-8">
                                      <Field
                                        name="customer.employment.permanent_contract"
                                        component={RadioButton}
                                        type="radio"
                                        value={true}
                                        disabled={disabled}
                                        parse={parseRadioFormField}
                                        label={t`No`}
                                      />
                                    </div>
                                  </div>
                                </div>

                                <Condition
                                  when="customer.employment.permanent_contract"
                                  is={false}>
                                  <div className="w-full md:w-1/2 px-2 mb-4">
                                    <Field
                                      name="customer.employment.job_ends_at"
                                      label={t`Contract end date`}
                                      component={Input}
                                      placeholder={t`DD.MM.YYYY`}
                                      disabled={disabled}
                                      format={formatDateFormField}
                                      parse={parseDateFormField}
                                      validate={composeFormValidators(
                                        isRequired(),
                                        isValidDate(),
                                      )}
                                    />
                                  </div>
                                </Condition>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </Block>

                  {showDeliveryAddressCc && (
                    <Block heading={t`Adresa livrare card`}>
                      <div className="pt-8 p-2">
                        <div className="md:flex md:flex-wrap hidden">
                          <div className="w-full md:w-1/2 px-2 mb-4">
                            <Field
                              name="customer.card_data.address_type"
                              label={t`Tip adresa`}
                              component={Select}
                              disabled={disabled}
                              validate={isRequired()}
                              /**
                               * by default 'Altele' is selected
                               **/
                              defaultValue="18408"
                              parse={value => {
                                const label = getCurrentStoredAddressLabel(
                                  values,
                                  value,
                                );

                                form.change(
                                  'customer.card_data.card_address',
                                  get(values, `customer.${label}`, {}),
                                );
                                return value;
                              }}>
                              <option
                                value=""
                                disabled>{t`Alege o adresa`}</option>
                              {addressType?.map(({name, code, display}) => {
                                return (
                                  <option
                                    key={`address-external-id-select-${code}`}
                                    value={code}
                                    hidden={!display}>
                                    {name}
                                  </option>
                                );
                              })}
                            </Field>
                          </div>
                        </div>

                        <Condition
                          when="customer.card_data.address_type"
                          is={addressType?.map(({code}) => code)}>
                          <Address
                            disabled={
                              getAddressDisabledStatus(cardDeliveryAddressId) ||
                              disabled
                            }
                            defaultValueLabel={getCurrentStoredAddressLabel(
                              values,
                              cardDeliveryAddressId,
                            )}
                            form={form}
                            uuid={uuid}
                            postalCodeRequired={true}
                          />
                        </Condition>
                      </div>
                    </Block>
                  )}

                  <Block heading={t`Request information`}>
                    <div className="pt-8 p-2">
                      <div className="md:flex md:flex-wrap">
                        <div className="w-full md:w-1/2 px-2 mb-4">
                          <Label value={t`Real Beneficiary`} />
                          <div className="flex mt-4">
                            <div className="pr-8">
                              <Field
                                name="detail.is_credit_beneficiary"
                                component={RadioButton}
                                type="radio"
                                value={true}
                                disabled={disabled}
                                parse={parseRadioFormField}
                                validate={composeFormValidators(isRequired())}
                                label={t`Yes`}
                              />
                            </div>
                          </div>
                        </div>

                        <div className="w-full md:w-1/2 px-2 mb-4">
                          <Field
                            name="detail.payment_day"
                            label={t`Preferred payment day`}
                            component={Select}
                            disabled={disabled}
                            type="number"
                            parse={parseNumberFormField}
                            format={formatNumberFormField}
                            validate={isRequired()}>
                            <option value="">{t`Choose a day`}</option>
                            {[...Array(25).keys()].map(dayNumber => (
                              <option
                                key={`paymentDay-select-${dayNumber + 1}`}
                                value={dayNumber + 1}>
                                {dayNumber + 1}
                              </option>
                            ))}
                          </Field>
                        </div>
                      </div>

                      <div className="md:flex md:flex-wrap">
                        <div className="w-full md:w-1/2 px-2 mb-4">
                          <Field
                            name="customer.mobile_phone"
                            label={t`Mobile number`}
                            component={Input}
                            disabled={true}
                            format={formatWhiteSpaceFormField}
                            validate={composeFormValidators(
                              isRequired(),
                              hasMaxLength(10),
                              isPhoneNumber(),
                            )}
                          />
                        </div>

                        <div className="w-full md:w-1/2 px-2 mb-4">
                          <Field
                            name="customer.email"
                            label={t`Email address`}
                            component={Input}
                            disabled={true}
                            validate={composeFormValidators(
                              isRequired(),
                              isEmail(),
                            )}
                          />
                        </div>
                      </div>

                      {(isConsumerFinance || isCreditCard) && (
                        <div className="md:flex md:flex-wrap">
                          <div className="w-full md:w-1/2 px-2 mb-4">
                            <Field
                              name="customer.detail.account_statement_delivery_mode"
                              label={t`Modalitate livrare extras cont`}
                              component={Select}
                              /**
                               * by default 'Email' is selected
                               * */

                              defaultValue={18100}
                              disabled={disabled || hasEmail}
                              parse={parseNumberFormField}
                              validate={isRequired()}>
                              <option value="">{t`Choose a method`}</option>
                              {accountStatementDeliveryMode?.map(
                                ({id, name}) => (
                                  <option
                                    key={`deliveryMode-select-${id}`}
                                    value={id}>
                                    {name}
                                  </option>
                                ),
                              )}
                            </Field>
                          </div>
                        </div>
                      )}
                    </div>

                    {isPersonalNeeds && (
                      <>
                        <div className="pt-8 p-2 border-t">
                          <div className="w-full px-2 mb-4">
                            <Label value={t`Metoda de debursare`} />
                            <div className="mt-4">
                              <Field
                                name="payment"
                                label={t`Payment with credit card`}
                                component={RadioButton}
                                type="radio"
                                value="card"
                                disabled={disabled || forcedIbanIds.size > 0}
                                helper={
                                  forcedIbanIds.size > 0
                                    ? 'Plata prin card bancar nu este disponibila' +
                                      '  pentru Contractul de Refinantare selectat.'
                                    : 'In functie de procedurile bancii emitente a cardului, efectuarea tranzactiei poate dura mai mult.' +
                                      ' Sunt permise doar carduri emise de banci romanesti.'
                                }
                              />
                            </div>

                            <div className="mt-4">
                              <Field
                                name="payment"
                                label={t`Payment with IBAN`}
                                component={RadioButton}
                                type="radio"
                                value="iban"
                                disabled={disabled}
                                helper="Plata in maxim 3 zile."
                              />
                            </div>
                          </div>

                          <Condition when="payment" is="iban">
                            <div className="w-full px-2 mb-4">
                              <Field
                                name="detail.bank_account"
                                label={t`Bank account`}
                                component={Input}
                                format={formatNonWhiteSpaceFormField}
                                disabled={disabled}
                                validate={composeFormValidators(
                                  isRequired(),
                                  isIBAN(),
                                )}
                              />
                            </div>

                            <div className="w-full px-2 mb-4">
                              <div className="border rounded">
                                <DocumentsUploadBox
                                  acceptFileTypes="image/*,.pdf"
                                  description={
                                    <>
                                      <span className="block text-14 leading-normal font-medium pb-2">
                                        {t`Upload proof of bank statement`}
                                      </span>
                                      <span className="block">
                                        {t`Accepted file types: ${'.pdf, .jpg, .jpeg, .png'}.`}
                                      </span>
                                    </>
                                  }
                                  isFileUploaded={
                                    uploaded[DOC_TYPE_BANK_STATEMENT]
                                  }
                                  isFileMissing={
                                    (submitFailed || submitSucceeded) &&
                                    !uploaded[DOC_TYPE_BANK_STATEMENT]
                                  }
                                  isLoading={uploading[DOC_TYPE_BANK_STATEMENT]}
                                  onUpload={f =>
                                    upload(f, DOC_TYPE_BANK_STATEMENT)
                                  }
                                  uploadInputLabel={t`Upload document`}
                                  documents={file[DOC_TYPE_BANK_STATEMENT]}
                                  showDownloadLink={true}
                                />
                              </div>
                            </div>
                          </Condition>
                        </div>
                      </>
                    )}
                  </Block>

                  <Block heading={t`Informatii CI`}>
                    <div className="pt-8 p-2">
                      <Address
                        displayFullAddressId={true}
                        hasInitialFullAddressId={hasInitialFullAddressId}
                        disabled={disabled}
                        defaultValueLabel="identity_card.address"
                        form={form}
                        uuid={uuid}
                      />

                      <div className="w-full px-2 mb-4">
                        <Field
                          name="customer.identity_card.birth_place"
                          label={t`Locul nasterii`}
                          component={Input}
                          disabled={hasInitialBirthPlace}
                          validate={isRequired()}
                        />
                      </div>
                    </div>
                  </Block>
                </div>

                <div className="lg:w-1/2 pl-10">
                  {!isEmployedWithANAF && (
                    <Block heading={t`Upload proof of income`}>
                      <DocumentsUploadBox
                        acceptFileTypes="image/*,.pdf"
                        description={t`Accepted file types: ${'.pdf, .jpg, .jpeg, .png'}.`}
                        isFileUploaded={uploaded[DOC_TYPE_INCOME]}
                        isFileMissing={
                          (submitFailed || submitSucceeded) &&
                          !uploaded[DOC_TYPE_INCOME]
                        }
                        isLoading={uploading[DOC_TYPE_INCOME]}
                        onUpload={f => upload(f, DOC_TYPE_INCOME)}
                        uploadInputLabel={t`Upload document`}
                        documents={file[DOC_TYPE_INCOME]}
                        showDownloadLink={true}
                      />
                    </Block>
                  )}

                  <Block heading={t`Current personal details`}>
                    <div className="pt-8 p-2">
                      <div className="md:flex md:flex-wrap">
                        <div className="w-full md:w-1/2 px-2 mb-4">
                          <Field
                            name="customer.detail.civil_status_id"
                            label={t`Marital status`}
                            component={Select}
                            disabled={disabled}
                            parse={parseNumberFormField}
                            validate={isRequired()}>
                            <option value="">{t`Choose a civil status`}</option>
                            {civilStatus &&
                              civilStatus.map(({id, name}) => (
                                <option
                                  key={`civilStatus-select-${id}`}
                                  value={id}>
                                  {name}
                                </option>
                              ))}
                          </Field>
                        </div>

                        <div className="w-full md:w-1/2 px-2 mb-4">
                          <Field
                            name="customer.detail.education_level_id"
                            label={t`Education level`}
                            parse={parseNumberFormField}
                            component={Select}
                            disabled={disabled}
                            validate={isRequired()}>
                            <option value="">{t`Choose education level`}</option>

                            {education &&
                              education.map(({id, name}) => (
                                <option
                                  key={`education-select-${id}`}
                                  value={id}>
                                  {name}
                                </option>
                              ))}
                          </Field>
                        </div>
                      </div>

                      <div className="md:flex md:flex-wrap">
                        <div className="w-full px-2 mb-4">
                          <Label value={t`Political Exposure`} />
                          <div className="flex mt-4">
                            <div className="pr-8">
                              <Field
                                name="customer.political.is_exposed"
                                component={RadioButton}
                                type="radio"
                                value={true}
                                disabled={disabled}
                                parse={parseRadioFormField}
                                label={t`Yes`}
                              />
                            </div>

                            <div className="pr-8">
                              <Field
                                name="customer.political.is_exposed"
                                component={RadioButton}
                                type="radio"
                                value={false}
                                disabled={disabled}
                                parse={parseRadioFormField}
                                label={t`No`}
                              />
                            </div>
                          </div>
                        </div>

                        <Condition
                          when="customer.political.is_exposed"
                          is={true}>
                          <div className="w-full px-2 mb-4">
                            <Field
                              name="customer.political.function"
                              label={t`Position held`}
                              component={Input}
                              disabled={disabled}
                              validate={composeFormValidators(
                                isRequired(),
                                isAlphaNumeric(),
                              )}
                            />
                          </div>
                        </Condition>
                      </div>
                    </div>

                    <div className="pt-8 p-2 border-t">
                      <div className="md:flex md:flex-wrap">
                        <div className="w-full md:w-1/2 px-2 mb-4">
                          <Field
                            name="customer.detail.property_type_id"
                            label={t`Home contract`}
                            parse={parseNumberFormField}
                            component={Select}
                            disabled={disabled}
                            validate={isRequired()}>
                            <option value="">{t`Choose a type`}</option>
                            {propertyStatus &&
                              propertyStatus.map(({id, name}) => (
                                <option
                                  key={`propertyStatus-select-${id}`}
                                  value={id}>
                                  {name}
                                </option>
                              ))}
                          </Field>
                        </div>
                      </div>

                      <div className="md:flex md:flex-wrap">
                        <div className="w-full px-2 mb-4">
                          <Label value={t`Address same as ID`} />
                          <div className="flex mt-4">
                            <div className="pr-8">
                              <Field
                                name="isCorrespondenceAddressSameAsId"
                                component={RadioButton}
                                type="radio"
                                value={true}
                                disabled={disabled}
                                parse={parseRadioFormField}
                                label={t`Yes`}
                              />
                            </div>

                            <div className="pr-8">
                              <Field
                                name="isCorrespondenceAddressSameAsId"
                                component={RadioButton}
                                type="radio"
                                value={false}
                                disabled={disabled}
                                label={t`No`}
                                parse={value => {
                                  values.customer.correspondence_address = null;
                                  return parseRadioFormField(value);
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <Condition
                        when="isCorrespondenceAddressSameAsId"
                        is={false}>
                        <Address
                          disabled={disabled}
                          defaultValueLabel="correspondence_address"
                          form={form}
                          uuid={uuid}
                        />
                      </Condition>

                      <div className="md:flex md:flex-wrap">
                        <div className="w-full px-2 mb-4">
                          <Label value={t`Residence address same as ID`} />
                          <div className="flex mt-4">
                            <div className="pr-8">
                              <label>
                                <Field
                                  name="isResidenceAddressSameAsId"
                                  label={t`Yes`}
                                  component={RadioButton}
                                  type="radio"
                                  value={true}
                                  disabled={disabled}
                                  parse={parseRadioFormField}
                                />
                              </label>
                            </div>

                            <div className="pr-8">
                              <label>
                                <Field
                                  name="isResidenceAddressSameAsId"
                                  label={t`No`}
                                  component={RadioButton}
                                  type="radio"
                                  value={false}
                                  disabled={disabled}
                                  parse={value => {
                                    values.customer.residence_address = null;
                                    return parseRadioFormField(value);
                                  }}
                                />
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>

                      <Condition when="isResidenceAddressSameAsId" is={false}>
                        <Address
                          disabled={disabled}
                          defaultValueLabel="residence_address"
                          form={form}
                          uuid={uuid}
                        />
                      </Condition>
                    </div>

                    <div className="pt-8 p-2 border-t">
                      <div className="w-full px-2 mb-4">
                        <Label value={t`Funding source`} />
                        <div className="md:flex md:flex-wrap mt-3">
                          <FieldArray
                            name="customer.employment.funding_source"
                            validate={hasItems()}>
                            {({fields, meta}) => (
                              <>
                                {fundingSource?.map(({id, name}) => {
                                  const fieldValueIndex =
                                    fields.value?.findIndex(
                                      ({funding_source_type_id}) =>
                                        funding_source_type_id === id,
                                    );

                                  return (
                                    <div
                                      className="w-full md:w-1/2 pr-4 py-1"
                                      key={id}>
                                      <Field
                                        name="customer.employment.funding_source"
                                        label={name}
                                        component={Checkbox}
                                        type="checkbox"
                                        checked={fieldValueIndex > -1}
                                        disabled={disabled}
                                        showError={false}
                                        onChange={({target: {checked}}) =>
                                          checked
                                            ? fields.push({
                                                funding_source_type_id: id,
                                              })
                                            : fields.remove(fieldValueIndex)
                                        }
                                      />
                                    </div>
                                  );
                                })}

                                {meta.error && meta.touched && (
                                  <div className="w-full">
                                    <ValidationError value={meta.error} />
                                  </div>
                                )}
                              </>
                            )}
                          </FieldArray>
                        </div>

                        <Condition
                          when="customer.employment.funding_source"
                          is={{funding_source_type_id: 18000}}>
                          <div className="w-full mt-3">
                            <Field
                              name="customer.employment.funding_source_custom"
                              label={t`Source of funds description`}
                              component={Input}
                              disabled={disabled}
                              validate={composeFormValidators(isRequired())}
                            />
                          </div>
                        </Condition>
                      </div>
                    </div>

                    {requestType !== 10 && (
                      <div className="pt-8 p-2 border-t">
                        <div className="md:flex md:flex-wrap">
                          <div className="w-full md:w-1/2 px-2 mb-4">
                            <Field
                              name="customer.alias"
                              label={t`Alias`}
                              component={Input}
                              disabled={disabled}
                              validate={composeFormValidators(isRequired())}
                            />
                          </div>

                          <div className="w-full md:w-1/2 px-2 mb-4">
                            <Field
                              name="customer.employment.job_title"
                              label={t`Occupation name`}
                              component={Input}
                              disabled={disabled}
                              validate={composeFormValidators(isRequired())}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </Block>

                  {isConsumerFinance && (
                    <Block
                      heading={t`Order summary`}
                      className="bg-purple-lightest text-pink">
                      <div className="p-8 px-4">
                        <Products
                          products={values.products}
                          total={values.detail?.loan_amount ?? 0}
                        />
                      </div>
                    </Block>
                  )}
                </div>
              </div>
            </form>
          </>
        );
      }}
    />
  );
};

export default AdditionalInformationForm;
